import React from "react";
import {useTranslation} from "react-i18next";
import {Firmware, FirmwareField} from "../../../app/api/model/firmware";
import {UseModal} from "../../../hooks/useModal";
import {
    ListRequest,
    useFirmwareCreateMutation,
    useFirmwareStateListQuery,
    useFirmwareTargetListQuery,
    useFirmwareUpdateMutation,
    useHardwareTypeListQuery,
} from "../../../app/api/api";
import * as yup from "yup";
import UpdateDialog from "../../../components/Dialog/UpdateDialog";
import {InputField, InputFieldType} from "../../../components/input/factory/types";
import {TFunction} from "i18next";

interface AddEditFirmwareDialogProps {
    modal: UseModal;
    firmware?: Firmware;
    onSuccess?: (id?: number) => void;
}
export function AddEditFirmwareDialog({modal, firmware, onSuccess}: AddEditFirmwareDialogProps) {
    const {t} = useTranslation();
    const [createFirmware] = useFirmwareCreateMutation();
    const [updateFirmware] = useFirmwareUpdateMutation();

    const handleSubmit = async (f: Partial<Firmware>) => {
        const response = !f[FirmwareField.ID] ? await createFirmware(f).unwrap() : await updateFirmware(f).unwrap();
        return response.id;
    };

    const getValidationSchema = (t: TFunction) =>
        yup.object({
            [FirmwareField.VERSION_NUMBER]: yup.string().required(t("validation_inputRequired")), //validation 19-20 digits
            [FirmwareField.STATE]: yup.string().required(t("validation_inputRequired")),
            [FirmwareField.HARDWARE_TYPE]: yup.string().required(t("validation_inputRequired")),
            [FirmwareField.TARGET]: yup.string().required(t("validation_inputRequired")),
            [FirmwareField.LEGACY]: yup.boolean(),
        });

    const initialValues = {
        [FirmwareField.ID]: firmware?.[FirmwareField.ID],
        [FirmwareField.VERSION_NUMBER]: firmware?.[FirmwareField.VERSION_NUMBER] || "",
        [FirmwareField.STATE]: firmware?.[FirmwareField.STATE] || "",
        [FirmwareField.HARDWARE_TYPE]: firmware?.[FirmwareField.HARDWARE_TYPE] || "",
        [FirmwareField.TARGET]: firmware?.[FirmwareField.TARGET] || "MCU",
        [FirmwareField.LEGACY]: firmware?.[FirmwareField.LEGACY] || false,
    } as Partial<Firmware>;

    return (
        <UpdateDialog
            titleKey={firmware ? "action_editFirmware" : "action_createFirmware"}
            modal={modal}
            validationSchema={getValidationSchema}
            inputFields={[
                {
                    fieldName: FirmwareField.ID,
                    type: InputFieldType.HIDDEN,
                    required: !!firmware,
                    translationKey: t("generic_id"),
                },
                {
                    translationKey: "firmware_version",
                    fieldName: FirmwareField.VERSION_NUMBER,
                    required: true,
                    gridProps: {xs: 6},
                },
                {
                    translationKey: "firmware_hardwareType",
                    fieldName: FirmwareField.HARDWARE_TYPE,
                    type: InputFieldType.SELECT_FROM_REQUEST,
                    query: useHardwareTypeListQuery,
                    request: {},
                    required: true,
                    gridProps: {xs: 6},
                } as InputField<Firmware, string, ListRequest>,
                {
                    translationKey: "firmware_state",
                    fieldName: FirmwareField.STATE,
                    type: InputFieldType.SELECT_FROM_REQUEST,
                    query: useFirmwareStateListQuery,
                    request: {},
                    required: true,
                    gridProps: {xs: 6},
                } as InputField<Firmware, string, ListRequest>,
                {
                    translationKey: "firmware_target",
                    fieldName: FirmwareField.TARGET,
                    type: InputFieldType.SELECT_FROM_REQUEST,
                    query: useFirmwareTargetListQuery,
                    request: {},
                    required: true,
                    gridProps: {xs: 6},
                } as InputField<Firmware, string, ListRequest>,
                {
                    translationKey: "firmware_legacy",
                    fieldName: FirmwareField.LEGACY,
                    type: InputFieldType.CHECKBOX,
                    gridProps: {xs: 6},
                },
            ]}
            initialValues={initialValues}
            onSuccess={onSuccess}
            onSubmit={handleSubmit}
        />
    );
}
